import axios from "axios";
import { useState } from "react";

type UseGoogleDrive = {
  accessToken: string | null;
};

export const useGoogleDrive = ({ accessToken }: UseGoogleDrive) => {
  const fetchFileListFromDrive = async (): Promise<File[] | null> => {
    if (!accessToken) return null;

    try {
      const response = await axios.get(
        "https://www.googleapis.com/drive/v3/files",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      console.log(response);
      return response.data.files;
    } catch (error) {
      console.error("Error fetching files:", error);
      return null;
    }
  };

  const fetchDirectoryList = async (
    directoryName: string
  ): Promise<string[] | null> => {
    if (!accessToken) return null;

    try {
      // 1. First, find the 'rvc_models' directory
      const rvcFolderResponse = await axios.get(
        "https://www.googleapis.com/drive/v3/files",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            q: `name='${directoryName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
            fields: "files(id, name)",
          },
        }
      );

      const rvcFolder = rvcFolderResponse.data.files[0];
      if (!rvcFolder) return null;

      // 2. Then, fetch all subdirectories within 'rvc_models'
      const subdirsResponse = await axios.get(
        "https://www.googleapis.com/drive/v3/files",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            q: `'${rvcFolder.id}' in parents and mimeType='application/vnd.google-apps.folder' and trashed=false`,
            fields: "files(id, name)",
          },
        }
      );

      // 3. Extract and return directory names
      return subdirsResponse.data.files.map((file: any) => file.name);
    } catch (error) {
      console.error("Error fetching RVC model directories:", error);
      return null;
    }
  };

  const uploadFileToDrive = async ({
    file,
    directory,
    parentDirectoryId,
  }: {
    file: File;
    directory?: string;
    parentDirectoryId?: string;
  }): Promise<boolean> => {
    if (!accessToken) return false;

    let directoryId: string | null = "";
    if (directory) {
      directoryId = await makeDirectory(directory, parentDirectoryId);
    }

    const metadata = {
      name: file.name,
      mimeType: file.type,
      parents: [directoryId || ""],
    };

    const form = new FormData();
    form.append(
      "metadata",
      new Blob([JSON.stringify(metadata)], { type: "application/json" })
    );
    form.append("file", file);

    try {
      await axios.post(
        "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
        form,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return true;
    } catch (error) {
      console.error("Error uploading file:", error);
      return false;
    }
  };

  const makeDirectory = async (
    directoryName: string,
    parentDirectoryId?: string
  ): Promise<string | null> => {
    if (!accessToken) return null;

    try {
      // 폴더 이름과 부모 폴더로 기존 폴더 검색
      const queryParts = [
        `name='${directoryName}'`,
        `mimeType='application/vnd.google-apps.folder'`,
        `trashed=false`,
      ];

      if (parentDirectoryId) {
        queryParts.push(`'${parentDirectoryId}' in parents`);
      }

      const searchResponse = await axios.get(
        "https://www.googleapis.com/drive/v3/files",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            q: queryParts.join(" and "),
            fields: "files(id, name)",
          },
        }
      );

      const existingFolders = searchResponse.data.files;

      if (existingFolders.length > 0) {
        // 이미 같은 이름의 폴더가 존재하면 그 폴더의 ID 반환
        return existingFolders[0].id;
      } else {
        // 폴더가 존재하지 않으면 새로 생성
        const metadata: any = {
          name: directoryName,
          mimeType: "application/vnd.google-apps.folder",
        };

        if (parentDirectoryId) {
          metadata.parents = [parentDirectoryId];
        }

        const createResponse = await axios.post(
          "https://www.googleapis.com/drive/v3/files",
          JSON.stringify(metadata),
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        return createResponse.data.id; // 새로 생성한 폴더 ID 반환
      }
    } catch (error) {
      console.error("Error searching or creating folder:", error);
      return null;
    }
  };
  return {
    fetchFileListFromDrive,
    fetchDirectoryList,
    uploadFileToDrive,
    makeDirectory,
  };
};
